import React, { useState, useEffect, useContext, useRef } from "react";
import "./AdminActionsCircle.scss";
import OwButton from "@components/shared/OwButton/OwButton";
import UiContext from "@contexts/UiContext";
import SupplierContext from "@contexts/SupplierContext";
import UserContext from "@contexts/UserContext";
import VendorContext from "@contexts/VendorContext";
import { ClickToCopy } from "@components/shared/ClickToCopy/ClickToCopy";
import OwLabel from "@components/shared/OwLabel/OwLabel";
import NotificationLevelEnum from "@models/NotificationLevelEnum";
import INotification from "@models/INotification";
import NotificationPositionEnum from "@models/NotificationPositionEnum";
import IAdminLoginResultDTO from "@models/DTO/AdminDTO/IAdminLoginResultDTO";
import AdminService from "@services/AdminService";
import { AdminLoginModal } from "@components/admin/AdminLoginModal/AdminLoginModal";
import { Socket } from "socket.io-client";
import EndpointsEnum from "@models/EndpointsEnum";
import IUpdateVendorDTO from "@models/DTO/IUpdateVendorDTO";
import IOnUpdateVendorDTO from "@models/DTO/IOnUpdateVendorDTO";
import VendorService from "@services/VendorService";
import UserService from "@services/UserService";

type Props = {
    socket: Socket;
};

type PanelSide = "left" | "right";

/**
 * Developer/QA preferences for the admin panel (which side it docks on, which sections are collapsed).
 * Persisted to localStorage so the layout survives reloads.
 */
type PanelPrefs = {
    side: PanelSide;
    collapsed: Record<string, boolean>;
};

const PANEL_PREFS_KEY = "ow_admin_tool_prefs";

const DEFAULT_PANEL_PREFS: PanelPrefs = {
    side: "right",
    collapsed: {},
};

const loadPanelPrefs = (): PanelPrefs => {
    try {
        const raw = localStorage.getItem(PANEL_PREFS_KEY);
        if (!raw) {
            return DEFAULT_PANEL_PREFS;
        }
        const parsed = JSON.parse(raw) as Partial<PanelPrefs>;
        return {
            side: parsed.side === "left" ? "left" : "right",
            collapsed: parsed.collapsed && typeof parsed.collapsed === "object" ? parsed.collapsed : {},
        };
    } catch {
        return DEFAULT_PANEL_PREFS;
    }
};

const savePanelPrefs = (prefs: PanelPrefs) => {
    try {
        localStorage.setItem(PANEL_PREFS_KEY, JSON.stringify(prefs));
    } catch {
        // localStorage may be unavailable (private mode, etc.) - prefs are a convenience only
    }
};

type SectionProps = {
    sectionKey: string;
    title: string;
    skipTranslate?: boolean;
    isCollapsed: boolean;
    onToggle: (sectionKey: string) => void;
    children: React.ReactNode;
};

/**
 * Collapsible panel section. Uses a plain <button> (not OwButton) so toggling is instant and
 * isn't subject to OwButton's double-click lockout.
 */
const Section = ({ sectionKey, title, skipTranslate, isCollapsed, onToggle, children }: SectionProps) => (
    <div className="section">
        <button type="button" className="section-header" aria-expanded={!isCollapsed} onClick={() => onToggle(sectionKey)}>
            <i className={"fas fa-fw " + (isCollapsed ? "fa-chevron-right" : "fa-chevron-down")} />
            <OwLabel title={title} isBold={true} noBottomMargin={true} skipTranslate={skipTranslate} />
        </button>
        {!isCollapsed && <div className="section-body">{children}</div>}
    </div>
);

type CopyRowProps = {
    label: string;
    value?: string | number | null;
};

/**
 * Label + truncated value + copy button. The value is shown (not just the label) so devs/QA can
 * eyeball a GUID/ID without having to copy and paste it somewhere.
 */
const CopyRow = ({ label, value }: CopyRowProps) => {
    const text = value === null || value === undefined ? "" : String(value);

    return (
        <ClickToCopy text={text}>
            <div className="copy-row flex-grow-1" title={text}>
                <span className="copy-label">{label}</span>
                <span className="copy-value">{text || "—"}</span>
            </div>
        </ClickToCopy>
    );
};

const formatIdleTime = (totalSeconds: number) => {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
};

export const AdminActionsCircle = ({ socket }: Props) => {
    const uiContext = useContext(UiContext);

    const supplierModel = useContext(SupplierContext);
    const userModel = useContext(UserContext);
    const vendorModel = useContext(VendorContext);

    const [isVisible, setIsVisible] = useState(true);
    const [isOpen, setIsOpen] = useState(false);
    const [viewport, setViewport] = useState({ width: 0, height: 0 });
    const [screen, setScreen] = useState({ width: 0, height: 0 });
    const [adminLogin, setAdminLogin] = useState<IAdminLoginResultDTO | null>(null);
    const [showAdminLoginModal, setShowAdminLoginModal] = useState(false);
    const [idleTimeDisplay, setIdleTimeDisplay] = useState(1800);
    const [panelPrefs, setPanelPrefs] = useState<PanelPrefs>(loadPanelPrefs);

    useEffect(() => {
        // Set initial values
        updateDimensions();

        // Add event listener
        window.addEventListener("resize", updateDimensions);

        // Load admin login from localStorage
        const savedAdminLogin = AdminService.getAdminLogin();
        if (savedAdminLogin) {
            setAdminLogin(savedAdminLogin);
        }

        // Cleanup
        return () => window.removeEventListener("resize", updateDimensions);
    }, []);

    useEffect(() => {
        const observer = new MutationObserver(() => {
            if (window.showAdmin) {
                setIsVisible(true);
            }
        });

        // Observe changes to the showHiddenFeatures property on the window object
        const observeShowAdminFeature = () => {
            Object.defineProperty(window, "showAdmin", {
                set: (value) => {
                    if (value === true) {
                        setIsVisible(true);
                    }
                },
                configurable: true, // Allow redefinition
            });
        };

        // Initial observation
        observeShowAdminFeature();

        // Clean up the observer
        return () => {
            observer.disconnect();
            // Reset the property instead of deleting
            Object.defineProperty(window, "showAdmin", {
                value: false,
                writable: true, // Allow modifications later if needed
                configurable: true,
                enumerable: true,
            });
        };
    }, [setIsVisible]);

    // Close the panel on Escape
    useEffect(() => {
        if (!isOpen) {
            return;
        }

        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape") {
                setIsOpen(false);
            }
        };

        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [isOpen]);

    const updateDimensions = () => {
        setViewport({
            width: window.innerWidth,
            height: window.innerHeight,
        });
        setScreen({
            width: window.screen.width,
            height: window.screen.height,
        });
    };

    const updatePanelPrefs = (next: PanelPrefs) => {
        setPanelPrefs(next);
        savePanelPrefs(next);
    };

    const switchSides = () => {
        updatePanelPrefs({
            ...panelPrefs,
            side: panelPrefs.side === "left" ? "right" : "left",
        });
    };

    const toggleSection = (sectionKey: string) => {
        updatePanelPrefs({
            ...panelPrefs,
            collapsed: {
                ...panelPrefs.collapsed,
                [sectionKey]: !panelPrefs.collapsed[sectionKey],
            },
        });
    };

    const isSectionCollapsed = (sectionKey: string) => !!panelPrefs.collapsed[sectionKey];

    // Hides the tool entirely (e.g. when screen-sharing with a customer). Intentionally silent:
    // restore with `window.showAdmin = true` in the console, or by reloading the page.
    const closeTool = () => {
        setIsOpen(false);
        setIsVisible(false);
    };

    const showToast = () => {
        const notification: INotification = {
            title: "toast_title",
            message: "toast_message",
            level: NotificationLevelEnum.success,
            position: NotificationPositionEnum.bc,
        };

        uiContext.notifyFn(null, notification);
    };

    const handleAdminLoginSuccess = (result: IAdminLoginResultDTO) => {
        AdminService.saveAdminLogin(result);
        setAdminLogin(result);
        setShowAdminLoginModal(false);
    };

    const onUpdateVendorRef = useRef<((data: IOnUpdateVendorDTO) => void) | null>(null);
    const [isSavingVendorFlag, setIsSavingVendorFlag] = useState(false);

    useEffect(() => {
        return () => {
            if (onUpdateVendorRef.current) {
                socket.off(EndpointsEnum.VendorsOnUpdateVendor, onUpdateVendorRef.current);
            }
        };
    }, [socket]);

    // Emergency escape hatch: flip is_ecomm_user on the currently loaded vendor without needing
    // the supplier-side VendorSidebar (works from the vendor's own login, including the ecomm view).
    const toggleIsEcommUser = () => {
        if (isSavingVendorFlag) {
            return;
        }

        const nextValue = !vendorModel.is_ecomm_user;

        if (!window.confirm(`Set is_ecomm_user to ${String(nextValue)} for "${vendorModel.name}"?`)) {
            return;
        }

        // billTo/shipTo/shippingDetails/shippingAccounts/tokenOptions are intentionally omitted —
        // the socket handler skips each of them when absent, leaving them untouched.
        const payload: Partial<IUpdateVendorDTO> = {
            userToken: userModel.token,
            userGuid: userModel.guid,
            supplierGuid: supplierModel.guid,
            vendorGuid: vendorModel.guid,
            name: vendorModel.name,
            email: vendorModel.email,
            options: {
                is_ecomm_user: nextValue,
                // updateVendorWithOptions nulls these two when they are absent from options,
                // so echo the current values back.
                route_id: vendorModel.route_id,
                qbo_grouped_invoice_type: vendorModel.Vendor_Integration?.qbo_grouped_invoice_type,
            },
        };

        // @ts-expect-error tokenGuid is not on IBaseDTO but the socket handler consumes it (see VendorSidebar.saveVendor)
        payload.tokenGuid = UserService.getSupplierVendorToken(vendorModel, supplierModel.id)?.guid;

        const onUpdateVendor = (data: IOnUpdateVendorDTO) => {
            onUpdateVendorRef.current = null;
            VendorService.saveVendorData(data.vendorModel);
            // Reload so UIService re-evaluates the ecomm redirect with the new flag.
            window.location.reload();
        };

        onUpdateVendorRef.current = onUpdateVendor;
        socket.once(EndpointsEnum.VendorsOnUpdateVendor, onUpdateVendor);
        setIsSavingVendorFlag(true);
        socket.emit(EndpointsEnum.VendorsUpdateVendor, payload);
    };

    const handleAdminLogout = () => {
        AdminService.clearAdminLogin();
        setAdminLogin(null);

        const notification: INotification = {
            title: "admin_logged_out",
            message: "admin_session_cleared",
            level: NotificationLevelEnum.info,
            position: NotificationPositionEnum.bc,
        };
        uiContext.notifyFn(null, notification);
    };

    // Poll the idle timer value every 500ms when the admin circle is open
    useEffect(() => {
        if (!isOpen || uiContext.disableIdleTimeout) {
            return;
        }

        const interval = setInterval(() => {
            const remaining = uiContext.getIdleTimeRemaining();
            setIdleTimeDisplay(remaining);
        }, 500);

        return () => {
            clearInterval(interval);
        };
    }, [isOpen, uiContext]);

    if (!supplierModel || !userModel || !vendorModel) {
        return null;
    }

    const sideClass = panelPrefs.side === "left" ? "side-left" : "side-right";

    return (
        <div className={`AdminActionsCircle d-print-none ${sideClass} ${isVisible ? "" : "d-none"}`}>
            <div className="circle" onClick={() => setIsOpen(!isOpen)}>
                <div className="icon">
                    Admin <i className="fas fa-cog"></i>
                </div>
            </div>

            {isOpen && (
                <div className="actions" role="dialog" aria-label="Admin tools">
                    <div className="panel-header">
                        <div className="panel-title">
                            <i className="fas fa-cog me-2" />
                            Admin Tools
                        </div>

                        <div className="panel-status" title="Idle timer">
                            <i className="fas fa-hourglass-half me-1" />
                            {uiContext.disableIdleTimeout ? <span className="text-warning">off</span> : formatIdleTime(idleTimeDisplay)}
                        </div>

                        <div className="panel-controls">
                            <button
                                type="button"
                                className="icon-btn"
                                onClick={switchSides}
                                title={panelPrefs.side === "left" ? "Dock right" : "Dock left"}>
                                <i
                                    className={
                                        "fas fa-fw " + (panelPrefs.side === "left" ? "fa-angle-double-right" : "fa-angle-double-left")
                                    }
                                />
                            </button>
                            <button type="button" className="icon-btn" onClick={() => setIsOpen(false)} title="Collapse panel (Esc)">
                                <i className="fas fa-fw fa-chevron-down" />
                            </button>
                            <button type="button" className="icon-btn icon-btn-danger" onClick={closeTool} title="Close tool">
                                <i className="fas fa-fw fa-times" />
                            </button>
                        </div>
                    </div>

                    <div className="panel-columns">
                        {/* Left column: things you DO */}
                        <div className="panel-column">
                            <Section
                                sectionKey="actions"
                                title="Actions"
                                skipTranslate={true}
                                isCollapsed={isSectionCollapsed("actions")}
                                onToggle={toggleSection}>
                                <div className="action-stack">
                                    <OwButton
                                        fn={() => {
                                            uiContext.setShowHiddenFeatures(!uiContext.showHiddenFeatures);
                                        }}
                                        title={"toggle_hidden_features"}
                                        postTitle={uiContext.showHiddenFeatures ? " (ON)" : " (OFF)"}
                                        className={uiContext.showHiddenFeatures ? "btn-success" : "btn-primary"}
                                        disableWidthOnClick={true}
                                    />
                                    <OwButton
                                        fn={() => {
                                            uiContext.setDisableIdleTimeout(!uiContext.disableIdleTimeout);
                                        }}
                                        title={uiContext.disableIdleTimeout ? "enable_idle_timeout" : "disable_idle_timeout"}
                                        className={uiContext.disableIdleTimeout ? "btn-warning" : "btn-primary"}
                                        disableWidthOnClick={true}
                                    />
                                    <OwButton fn={showToast} title={"show_toast"} disableWidthOnClick={true} />
                                </div>
                            </Section>

                            <Section
                                sectionKey="admin"
                                title="admin_login"
                                isCollapsed={isSectionCollapsed("admin")}
                                onToggle={toggleSection}>
                                {!adminLogin ? (
                                    <div className="action-stack">
                                        <OwButton
                                            fn={() => setShowAdminLoginModal(true)}
                                            title={"admin_login"}
                                            disableWidthOnClick={true}
                                        />
                                    </div>
                                ) : (
                                    <div>
                                        <div className="text-success small fw-bold">
                                            <i className="fas fa-check-circle me-1" />
                                            <OwLabel title="admin_logged_in" noBottomMargin={true} />
                                        </div>
                                        <CopyRow label="Email" value={adminLogin.admin?.email} />
                                        <div className="action-stack mt-1">
                                            <OwButton
                                                fn={handleAdminLogout}
                                                title={"admin_logout"}
                                                className="btn-danger"
                                                disableWidthOnClick={true}
                                            />
                                        </div>
                                    </div>
                                )}
                            </Section>

                            <Section
                                sectionKey="flags"
                                title="Account Flags"
                                skipTranslate={true}
                                isCollapsed={isSectionCollapsed("flags")}
                                onToggle={toggleSection}>
                                <div className="flag-row">
                                    <span className="copy-label">is_ecomm_user</span>
                                    <span className={"badge " + (vendorModel.is_ecomm_user ? "bg-success" : "bg-secondary")}>
                                        {String(!!vendorModel.is_ecomm_user)}
                                    </span>
                                </div>
                                <div className="action-stack mt-1">
                                    <OwButton
                                        title={
                                            isSavingVendorFlag
                                                ? "saving..."
                                                : vendorModel.is_ecomm_user
                                                  ? "disable is_ecomm_user"
                                                  : "enable is_ecomm_user"
                                        }
                                        fn={toggleIsEcommUser}
                                        skipTranslate={true}
                                        className="btn-danger"
                                        disabled={isSavingVendorFlag}
                                        disableWidthOnClick={true}
                                    />
                                </div>
                            </Section>
                        </div>

                        {/* Right column: things you LOOK UP */}
                        <div className="panel-column">
                            <Section
                                sectionKey="supplier"
                                title="supplier"
                                isCollapsed={isSectionCollapsed("supplier")}
                                onToggle={toggleSection}>
                                <CopyRow label="Name" value={supplierModel.name} />
                                <CopyRow label="GUID" value={supplierModel.guid} />
                                <CopyRow label="ID" value={supplierModel.id} />
                                <button type="button" className="link-btn" onClick={() => console.log("supplierModel", supplierModel)}>
                                    <i className="fas fa-terminal me-1" />
                                    console.log(supplierModel)
                                </button>
                            </Section>

                            <Section
                                sectionKey="account"
                                title="account"
                                isCollapsed={isSectionCollapsed("account")}
                                onToggle={toggleSection}>
                                <CopyRow label="Name" value={vendorModel.name} />
                                <CopyRow label="GUID" value={vendorModel.guid} />
                                <CopyRow label="ID" value={vendorModel.id} />
                                <button type="button" className="link-btn" onClick={() => console.log("vendorModel", vendorModel)}>
                                    <i className="fas fa-terminal me-1" />
                                    console.log(vendorModel)
                                </button>
                            </Section>

                            <Section sectionKey="user" title="user" isCollapsed={isSectionCollapsed("user")} onToggle={toggleSection}>
                                <CopyRow label="Name" value={userModel.name} />
                                <CopyRow label="Email" value={userModel.email} />
                                <CopyRow label="GUID" value={userModel.guid} />
                                <CopyRow label="ID" value={userModel.id} />
                                <button type="button" className="link-btn" onClick={() => console.log("userModel", userModel)}>
                                    <i className="fas fa-terminal me-1" />
                                    console.log(userModel)
                                </button>
                            </Section>

                            <Section
                                sectionKey="resolution"
                                title="Resolution"
                                skipTranslate={true}
                                isCollapsed={isSectionCollapsed("resolution")}
                                onToggle={toggleSection}>
                                <CopyRow label="Viewport" value={`${viewport.width}x${viewport.height}`} />
                                <CopyRow label="Screen" value={`${screen.width}x${screen.height}`} />
                            </Section>
                        </div>
                    </div>
                </div>
            )}

            {showAdminLoginModal && (
                <AdminLoginModal
                    closeModal={() => setShowAdminLoginModal(false)}
                    onLoginSuccess={handleAdminLoginSuccess}
                    notifyFn={uiContext.notifyFn}
                />
            )}
        </div>
    );
};
